import React, { useState, useRef, useEffect } from "react";
import "./Google.css";
import { IoMdClose } from "react-icons/io";

export default function PricingPlans() {
  const [activeTab, setActiveTab] = useState("monthly");
  const [selectedPlan, setSelectedPlan] = useState(null);
  const popupRef = useRef(null); // 🔹 Reference for popup box

  // ✅ Close popup when clicking outside
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        setSelectedPlan(null);
      }
    };
    if (selectedPlan) {
      document.addEventListener("mousedown", handleOutsideClick);
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
    }
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [selectedPlan]);

  // Shared full details (same across durations)
  const fullDetails = {
    resources: [
      { label: "Dedicated Resources", value: "" }, // values updated per plan below
    ],
    setup: [
      "WhatsApp Group Creation - Team Collaboration ✔",
      "0 Day Report for MOM Growth Comparison ✔",
      "Google Analytics - Setup and Review ✔",
      "Google Search Console - Setup and Review ✔",
      "Google Penalty Check ✔",
      "Website Indexing Check ✔",
      "Business Brief Meeting ✔",
      "Sitemap Submission ✔"
    ],
    onpage_seo: [],
    offpage_seo: [],
    technical_seo: [],
    reporting: [],
    support: []
  };

  // Build full details per plan (from screenshots)
  const planDetails = {
    lite: {
      resources: [
        { label: "Dedicated Resources", value: "1" },
        { label: "Google Ads Executive", value: "1" },
        { label: "Asst. Executive", value: "0" },
        { label: "Shared Resources", value: "2" },
        { label: "Account Manager", value: "❌" },
        { label: "Graphic Designer", value: "1" },
        { label: "Copy Writer", value: "1" }
      ],

      initial_setup_and_analysis: [
        { label: "WhatsApp Group Creation - Team Collaboration", value: "✔" },
        { label: "0 Day Report for MOM Growth Comparison", value: "✔" },
        { label: "Business Brief Meeting", value: "✔" },
        { label: "Account Creation/Overhaul", value: "✔" },
        { label: "Keyword Research", value: "✔" },
        { label: "Ad Extensions Setup", value: "✔" },
        { label: "Audience Research", value: "✔" },
        { label: "Retargeting Setup", value: "❌" },
        { label: "Google Analytics Integration", value: "✔" },
        { label: "Conversion Tracking Setup", value: "✔" }
      ],

      ad_creation_and_optimization: [
        { label: "Search Ads", value: "✔" },
        { label: "Performance Max", value: "❌" },
        { label: "Display Network Advertising", value: "✔" },
        { label: "YouTube Video Advertising", value: "❌" },
        { label: "Google Shopping Campaigns", value: "❌" },
        { label: "App Campaigns", value: "❌" },
        { label: "GMB Ads", value: "❌" },
        { label: "Dynamic Search Ads", value: "✔" }
      ],

      targeting_and_retargting: [
        { label: "Geographic Targeting", value: "✔" },
        { label: "Demographic Targeting", value: "✔" },
        { label: "Interest-Based Targeting", value: "✔" },
        { label: "Behavioral Targeting", value: "✔" },
        { label: "Retargeting Campaign Setup", value: "✔" },
        { label: "Budget Management", value: "✔" },
        { label: "Max. Ads Budget Management", value: "100000" },
        { label: "CPC Optimization", value: "❌" },
        { label: "CPA Optimization", value: "❌" }
      ],

      analytics_and_reporting: [
        { label: "Planning", value: "Basic" },
        { label: "Strategy", value: "Basic" },
        { label: "Real-Time Monitoring", value: "❌" },
        { label: "Weekly Performance Reports", value: "❌" },
        { label: "In-depth Monthly Reports", value: "✔" },
        { label: "Conversion Tracking", value: "Basic" },
        { label: "ROAS Analysis", value: "✔" },
        { label: "Daily Updates in Group", value: "❌" }
      ],

      support_and_consultation: [
        { label: "Phone Support", value: "✔" },
        { label: "Chat Support", value: "✔" },
        { label: "Email Support", value: "✔" },
        { label: "Monthly Consultation", value: "1 hour" },
        { label: "PRP (Performance Review & Planning)", value: "Monthly" }
      ],
    },

    standard: {
      resources: [
        { label: "Dedicated Resources", value: "2" },
        { label: "Google Ads Executive", value: "1" },
        { label: "Asst. Executive", value: "1" },
        { label: "Shared Resources", value: "2" },
        { label: "Account Manager", value: "❌" },
        { label: "Graphic Designer", value: "1" },
        { label: "Copy Writer", value: "1" }
      ],

      initial_setup_and_analysis: [
        { label: "WhatsApp Group Creation - Team Collaboration", value: "✔" },
        { label: "0 Day Report for MOM Growth Comparison", value: "✔" },
        { label: "Business Brief Meeting", value: "✔" },
        { label: "Account Creation/Overhaul", value: "✔" },
        { label: "Keyword Research", value: "✔" },
        { label: "Ad Extensions Setup", value: "✔" },
        { label: "Audience Research", value: "✔" },
        { label: "Retargeting Setup", value: "Basic" },
        { label: "Google Analytics Integration", value: "✔" },
        { label: "Conversion Tracking Setup", value: "✔" }
      ],

      ad_creation_and_optimization: [
        { label: "Search Ads", value: "✔" },
        { label: "Performance Max", value: "❌" },
        { label: "Display Network Advertising", value: "✔" },
        { label: "YouTube Video Advertising", value: "❌" },
        { label: "Google Shopping Campaigns", value: "❌" },
        { label: "App Campaigns", value: "❌" },
        { label: "GMB Ads", value: "✔" },
        { label: "Dynamic Search Ads", value: "✔" }
      ],

      targeting_and_retargting: [
        { label: "Geographic Targeting", value: "✔" },
        { label: "Demographic Targeting", value: "✔" },
        { label: "Interest-Based Targeting", value: "✔" },
        { label: "Behavioral Targeting", value: "✔" },
        { label: "Retargeting Campaign Setup", value: "✔" },
        { label: "Budget Management", value: "✔" },
        { label: "Max. Ads Budget Management", value: "150000" },
        { label: "CPC Optimization", value: "✔" },
        { label: "CPA Optimization", value: "✔" }
      ],

      analytics_and_reporting: [
        { label: "Planning", value: "Intermediate" },
        { label: "Strategy", value: "Intermediate" },
        { label: "Real-Time Monitoring", value: "✔" },
        { label: "Weekly Performance Reports", value: "❌" },
        { label: "In-depth Monthly Reports", value: "✔" },
        { label: "Conversion Tracking", value: "Advanced" },
        { label: "ROAS Analysis", value: "✔" },
        { label: "Daily Updates in Group", value: "❌" }
      ],

      support_and_consultation: [
        { label: "Phone Support", value: "✔" },
        { label: "Chat Support", value: "✔" },
        { label: "Email Support", value: "✔" },
        { label: "Monthly Consultation", value: "2 hours" },
        { label: "PRP (Performance Review & Planning)", value: "Monthly" }
      ]
    },

    advance: {
      resources: [
        { label: "Dedicated Resources", value: "2" },
        { label: "Google Ads Executive", value: "1" },
        { label: "Asst. Executive", value: "1" },
        { label: "Shared Resources", value: "3" },
        { label: "Account Manager", value: "1" },
        { label: "Graphic Designer", value: "1" },
        { label: "Copy Writer", value: "1" }
      ],

      initial_setup_and_analysis: [
        { label: "WhatsApp Group Creation - Team Collaboration", value: "✔" },
        { label: "0 Day Report for MOM Growth Comparison", value: "✔" },
        { label: "Business Brief Meeting", value: "✔" },
        { label: "Account Creation/Overhaul", value: "✔" },
        { label: "Keyword Research", value: "Comprehensive" },
        { label: "Ad Extensions Setup", value: "Comprehensive" },
        { label: "Audience Research", value: "Comprehensive" },
        { label: "Retargeting Setup", value: "Advanced" },
        { label: "Google Analytics Integration", value: "✔" },
        { label: "Conversion Tracking Setup", value: "✔" }
      ],

      ad_creation_and_optimization: [
        { label: "Search Ads", value: "✔" },
        { label: "Performance Max", value: "✔" },
        { label: "Display Network Advertising", value: "✔" },
        { label: "YouTube Video Advertising", value: "✔" },
        { label: "Google Shopping Campaigns", value: "✔" },
        { label: "App Campaigns", value: "❌" },
        { label: "GMB Ads", value: "✔" },
        { label: "Dynamic Search Ads", value: "✔" }
      ],

      targeting_and_retargting: [
        { label: "Geographic Targeting", value: "✔" },
        { label: "Demographic Targeting", value: "✔" },
        { label: "Interest-Based Targeting", value: "✔" },
        { label: "Behavioral Targeting", value: "✔" },
        { label: "Retargeting Campaign Setup", value: "✔" },
        { label: "Budget Management", value: "✔" },
        { label: "Max. Ads Budget Management", value: "250000" },
        { label: "CPC Optimization", value: "✔" },
        { label: "CPA Optimization", value: "✔" }
      ],

      analytics_and_reporting: [
        { label: "Planning", value: "Advanced" },
        { label: "Strategy", value: "Advanced" },
        { label: "Real-Time Monitoring", value: "✔" },
        { label: "Weekly Performance Reports", value: "❌" },
        { label: "In-depth Monthly Reports", value: "✔" },
        { label: "Conversion Tracking", value: "Comprehensive" },
        { label: "ROAS Analysis", value: "✔" },
        { label: "Daily Updates in Group", value: "✔" }
      ],

      support_and_consultation: [
        { label: "Phone Support", value: "✔" },
        { label: "Chat Support", value: "✔" },
        { label: "Email Support", value: "✔" },
        { label: "Monthly Consultation", value: "4 hours" },
        { label: "PRP (Performance Review & Planning)", value: "Monthly" }
      ]
    },

    enterprise: {
      resources: [
        { label: "Dedicated Resources", value: "2" },
        { label: "Google Ads Executive", value: "1" },
        { label: "Asst. Executive", value: "1" },
        { label: "Shared Resources", value: "3" },
        { label: "Account Manager", value: "1" },
        { label: "Graphic Designer", value: "1" },
        { label: "Copy Writer", value: "1" }
      ],

      initial_setup_and_analysis: [
        { label: "WhatsApp Group Creation - Team Collaboration", value: "✔" },
        { label: "0 Day Report for MOM Growth Comparison", value: "✔" },
        { label: "Business Brief Meeting", value: "✔" },
        { label: "Account Creation/Overhaul", value: "✔" },
        { label: "Keyword Research", value: "In-depth" },
        { label: "Ad Extensions Setup", value: "Comprehensive" },
        { label: "Audience Research", value: "In-depth" },
        { label: "Retargeting Setup", value: "Comprehensive" },
        { label: "Google Analytics Integration", value: "✔" },
        { label: "Conversion Tracking Setup", value: "✔" }
      ],

      ad_creation_and_optimization: [
        { label: "Search Ads", value: "✔" },
        { label: "Performance Max", value: "✔" },
        { label: "Display Network Advertising", value: "✔" },
        { label: "YouTube Video Advertising", value: "✔" },
        { label: "Google Shopping Campaigns", value: "✔" },
        { label: "App Campaigns", value: "✔" },
        { label: "GMB Ads", value: "✔" },
        { label: "Dynamic Search Ads", value: "✔" }
      ],

      targeting_and_retargting: [
        { label: "Geographic Targeting", value: "✔" },
        { label: "Demographic Targeting", value: "✔" },
        { label: "Interest-Based Targeting", value: "✔" },
        { label: "Behavioral Targeting", value: "✔" },
        { label: "Retargeting Campaign Setup", value: "✔" },
        { label: "Budget Management", value: "✔" },
        { label: "Max. Ads Budget Management", value: "1200000" },
        { label: "CPC Optimization", value: "✔" },
        { label: "CPA Optimization", value: "✔" }
      ],

      analytics_and_reporting: [
        { label: "Planning", value: "Comprehensive" },
        { label: "Strategy", value: "Comprehensive" },
        { label: "Real-Time Monitoring", value: "✔" },
        { label: "Weekly Performance Reports", value: "✔" },
        { label: "In-depth Monthly Reports", value: "Custom Reports" },
        { label: "Conversion Tracking", value: "Comprehensive" },
        { label: "ROAS Analysis", value: "Customized" },
        { label: "Daily Updates in Group", value: "✔" }
      ],

      support_and_consultation: [
        { label: "Phone Support", value: "✔" },
        { label: "Chat Support", value: "✔" },
        { label: "Email Support", value: "✔" },
        { label: "Monthly Consultation", value: "8 hours" },
        { label: "PRP (Performance Review & Planning)", value: "Monthly" }
      ]
    }

  };

  // Price table for each duration (from your screenshots / confirmed)
  const priceTable = {
    monthly: {
      lite: "₹20,000",
      standard: "₹35,000",
      advance: "₹50,000",
      enterprise: "₹150,000"
    },
    quarterly: {
      lite: "₹18,000",
      standard: "₹31,500",
      advance: "₹45,000",
      enterprise: "₹135,000"
    },
    halfyear: {
      lite: "₹16,000",
      standard: "₹28,000",
      advance: "₹40,000",
      enterprise: "₹120,000"
    },
    yearly: {
      lite: "₹14,000",
      standard: "₹24,500",
      advance: "₹35,000",
      enterprise: "₹105,000"
    }
  };

  // Build plans array from priceTable for the active tab
  const planMeta = [
    { id: "lite", title: "LITE", type: "Small Business" },
    { id: "standard", title: "STANDARD", type: "Moderate Competition" },
    { id: "advance", title: "ADVANCE", type: "Ecommerce / High Competition" },
    { id: "enterprise", title: "ENTERPRISE", type: "Brands / High Competition" }
  ];

  const plans = planMeta.map(p => ({
    id: p.id,
    title: p.title,
    price: priceTable[activeTab][p.id],
    type: p.type,
    features: (() => {
      if (p.id === "lite") return [
        "Dedicated Google Ads Executive, Shared Graphic Designer, Copy Writer",
        "Initial Setup & Strategy",
        "100000 Max Ad budget management",
        "Basic Strategy planning & conversion tracking",
        "1-hour monthly consultation and PRP"
      ];
      if (p.id === "standard") return [
        "Dedicated Google Ads Executive & Assistant, Shared Graphic Designer",
        "Initial Setup & Strategy & Basic Retargeting Setup",
        "150000 Max Ad budget management",
        "Intermediate Strategy planning, Advanced conversion tracking",
        "2 hours monthly consultation and PRP"
      ];
      if (p.id === "advance") return [
        "Dedicated Google Ads Executive & Assistant, Shared Account Manager",
        "Advanced Retargeting Setup, Comprehensive Keyword research & Ad experimentation",
        "250000 Max Ad budget management",
        "Advanced Strategy planning, Comprehensive conversion tracking",
        "4 hours Monthly Consultation and PRP"
      ];
      return [
        "Dedicated Google Ads Executive & Assistant, Shared Account Manage...",
        "Comprehensive Retargeting Setup, Keyword research & In-depth Ad e...",
        "1200000 Max Ad budget management",
        "Comprehensive Strategy planning & conversion tracking",
        "8 hours monthly consultation and PRP"
      ];
    })(),
    details: planDetails[p.id]
  }));

  return (
    <div className="pricing-wrapper">
      <section className="price-header" />
      <section className="services-header">
        <div className="photo-text">Google Ads
          <p>
            Packages & Plans
          </p>
        </div>
      </section>

      <div className="tabs">
        {[
          { id: "monthly", label: <>Month <br /> (1 Month)</> },
          { id: "quarterly", label: <>Quarterly <br /> (3 Months)</> },
          { id: "halfyear", label: <>Half Yearly <br /> (6 Months)</> },
          { id: "yearly", label: <>Yearly <br /> (12 Months)</> },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`tab-btn ${activeTab === tab.id ? "active" : ""}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="plans-grid">
        {plans.map(plan => (
          <div key={plan.id} className="plan-card">
            {/* POPULAR TAG */}
            {plan.id === "standard" && <div className="popular-tag">Popular</div>}

            {/* DISCOUNT + PRICE */}
            <h3 className="plan-title">{plan.title}</h3>

            <div className="price-section">
              {/* Show original price & discount only if NOT monthly */}
              {activeTab !== "monthly" && (
                <>
                  <p className="original-price">
                    ₹{(() => {
                      const monthlyPrices = {
                        lite: 20000,
                        standard: 35000,
                        advance: 50000,
                        enterprise: 150000
                      };

                      // Multiplying months so original price = base × months
                      const multipliers = {
                        quarterly: 1,
                        halfyear: 1,
                        yearly: 1
                      };

                      return (monthlyPrices[plan.id] * multipliers[activeTab]).toLocaleString();
                    })()}
                  </p>

                  {/* Dynamic discount badge */}
                  <span className="discount-badge">
                    {activeTab === "quarterly" && "10% Off"}
                    {activeTab === "halfyear" && "20% Off"}
                    {activeTab === "yearly" && "30% Off"}
                  </span>
                </>
              )}
            </div>



            <h2 className="plan-price">{plan.price}</h2>
            <p className="per-month">/Month</p>
            <button className="quote-btn">Get Quote</button>

            <hr className="divider" />
            <p className="plan-type">{plan.type}</p>

            <ul className="feature-list">
              {plan.features.map((f, idx) => (
                <li key={idx}>{f}</li>
              ))}
            </ul>

            <button className="details-btn" onClick={() => setSelectedPlan(plan)}>
              <span className="details-btn-txt">View Details</span>
            </button>
          </div>

        ))}
      </div>

      {selectedPlan && (
        <div className="popup-overlay">
          <div className="popup-box">
            <button className="close-btn" onClick={() => setSelectedPlan(null)}><IoMdClose /></button>
            <h2 className="popup-title">{selectedPlan.title}</h2>

            <div className="popup-grid">
              {/* Resources */}
              <div className="card">
                <h3 className="popup-subtitle">Resources</h3>
                <ul>
                  {selectedPlan.details.resources.map((r, i) => (
                    <li key={i}>
                      <span className="left">{r.label}</span>
                      <span className="sep">:</span>
                      <span className="right">{r.value}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Initial Setup & Analysis */}
              <div className="card">
                <h3 className="popup-subtitle">Initial Setup & Analysis</h3>
                <ul>
                  {selectedPlan.details.initial_setup_and_analysis.map((item, i) => (
                    <li key={i}>
                      <span className="left">{item.label}</span>
                      <span className="sep">:</span>
                      <span className="right">{item.value}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Ad Creation & Optimization */}
              <div className="card">
                <h3 className="popup-subtitle">Ad Creation & Optimization</h3>
                <ul>
                  {selectedPlan.details.ad_creation_and_optimization.map((item, i) => (
                    <li key={i}>
                      <span className="left">{item.label}</span>
                      <span className="sep">:</span>
                      <span className="right">{item.value}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Targeting & Retargeting */}
              <div className="card">
                <h3 className="popup-subtitle">Targeting & Retargeting</h3>
                <ul>
                  {selectedPlan.details.targeting_and_retargting.map((item, i) => (
                    <li key={i}>
                      <span className="left">{item.label}</span>
                      <span className="sep">:</span>
                      <span className="right">{item.value}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Analytics & Reporting */}
              <div className="card">
                <h3 className="popup-subtitle">Analytics & Reporting</h3>
                <ul>
                  {selectedPlan.details.analytics_and_reporting.map((item, i) => (
                    <li key={i}>
                      <span className="left">{item.label}</span>
                      <span className="sep">:</span>
                      <span className="right">{item.value}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Support & Consultation */}
              {selectedPlan.details.support_and_consultation &&
                selectedPlan.details.support_and_consultation.length > 0 && (
                  <div className="card">
                    <h3 className="popup-subtitle">Support & Consultation</h3>
                    <ul>
                      {selectedPlan.details.support_and_consultation.map((item, i) => (
                        <li key={i}>
                          <span className="left">{item.label}</span>
                          <span className="sep">:</span>
                          <span className="right">{item.value}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
            </div>


            <a
              href={`https://wa.me/919624883636?text=${encodeURIComponent(
                `Hi! I'm interested in the ${selectedPlan.title} plan (${activeTab.toUpperCase()}). Please share more details.`
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="subscribe-btn"
            >
              Subscribe Now
            </a>

          </div>
        </div>
      )}
    </div>
  );
}
